// ensure-gitnexus-installed-and-latest.ts
//
// GitNexus = hạ tầng CỐT LÕI của Avatar (PR gate, impact analysis dựa vào nó).
// Vì vậy: cài MẶC ĐỊNH không hỏi, và LUÔN giữ bản mới nhất.
//
// Hành vi (user chốt 2026-06-13):
//   - Chưa cài  → cài @latest, KHÔNG hỏi consent.
//   - Đã cài    → npm view bản latest, lệch → auto npm i -g gitnexus@latest.
//   - FAIL-SOFT toàn bộ: npm lỗi (quyền/mạng) → warn + tiếp, KHÔNG chặn init/sync.
//
// Tách module để init + sync dùng chung (DRY). spawn async (không block event loop)
// cho version check; install giữ installGitnexusViaNpm (stdio inherit, hiếm chạy).

import {
  detectGitnexusInstallation,
  invalidateGitnexusInstallationCache,
} from "./detect-gitnexus-installation.js";
import { installGitnexusViaNpm } from "./install-gitnexus-via-npm.js";
import { spawnAsyncCapture } from "./spawn-async-capture-with-timeout.js";
import { log } from "./terminal-logger.js";

const GITNEXUS_PACKAGE = "gitnexus";
const NPM_VIEW_TIMEOUT_MS = 15_000;
const NPM_UPDATE_TIMEOUT_MS = 5 * 60 * 1000;

export interface EnsureGitnexusResult {
  installed: boolean;
  version: string | null;
  updated: boolean; // có vừa nâng version không
  reason?: string; // lý do nếu không cài được (fail-soft)
}

// Lấy version mới nhất của gitnexus trên npm registry. null nếu offline/lỗi.
async function fetchLatestNpmVersion(): Promise<string | null> {
  try {
    const r = await spawnAsyncCapture("npm", ["view", `${GITNEXUS_PACKAGE}@latest`, "version"], {
      timeoutMs: NPM_VIEW_TIMEOUT_MS,
    });
    if (r.status !== 0 || !r.stdout) return null;
    const last = r.stdout
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .at(-1);
    return last && /^\d+\.\d+\.\d+/.test(last) ? last : null;
  } catch {
    return null;
  }
}

// npm i -g gitnexus@latest (async, stdio inherit để user thấy progress).
async function updateToLatest(): Promise<boolean> {
  try {
    const r = await spawnAsyncCapture("npm", ["install", "-g", `${GITNEXUS_PACKAGE}@latest`], {
      timeoutMs: NPM_UPDATE_TIMEOUT_MS,
    });
    return r.status === 0;
  } catch {
    return false;
  }
}

// Đảm bảo gitnexus đã cài + mới nhất. KHÔNG hỏi user. Fail-soft mọi nhánh.
export async function ensureGitnexusInstalledAndLatest(): Promise<EnsureGitnexusResult> {
  let info = detectGitnexusInstallation();

  // (1) Chưa cài → cài thẳng (không hỏi consent — GitNexus là core).
  if (!info.installed) {
    log.info("GitNexus chưa cài — đang cài (hạ tầng cốt lõi của Avatar, cài mặc định)...");
    try {
      const res = installGitnexusViaNpm(); // throws nếu fail
      return { installed: true, version: res.version, updated: true };
    } catch (err) {
      // Fail-soft: warn + tiếp, KHÔNG chặn init/sync.
      const msg = err instanceof Error ? err.message : String(err);
      log.warn(`Không cài được GitNexus (workspace vẫn dùng được): ${msg}`);
      log.dim("Cài lại sau: avatar gitnexus install (hoặc npm i -g gitnexus).");
      return { installed: false, version: null, updated: false, reason: "install-failed" };
    }
  }

  // (2) Đã cài → check latest, lệch thì auto-update.
  const latest = await fetchLatestNpmVersion();
  if (latest && info.version && latest !== info.version) {
    log.info(`GitNexus có bản mới (${info.version} → ${latest}) — đang cập nhật...`);
    const ok = await updateToLatest();
    if (ok) {
      invalidateGitnexusInstallationCache();
      info = detectGitnexusInstallation();
      log.success(`GitNexus đã cập nhật → v${info.version ?? latest}`);
      return { installed: true, version: info.version, updated: true };
    }
    // Update fail → fail-soft, dùng bản cũ.
    log.warn(`Không cập nhật được GitNexus (dùng bản cũ v${info.version}).`);
    return { installed: true, version: info.version, updated: false, reason: "update-failed" };
  }

  // Đã mới nhất / không lấy được latest (offline) → dùng bản đang có.
  return { installed: true, version: info.version, updated: false };
}
